import { useCallback, useRef, useState } from 'react'
import useDebounceCallback from './useDebounceCallback'
import { useIsomorphicEffect } from './useIsomorphicEffect'

interface UseBreakpointDetectorProps {
  breakpoint: number
  debounceTime?: number
}

const useBreakpointDetector = ({ breakpoint, debounceTime = 0 }: UseBreakpointDetectorProps) => {
  const [isBelowBreakpoint, setIsBelowBreakpoint] = useState(false)
  const elementRef = useRef<HTMLDivElement | null>(null)

  const debounce = useDebounceCallback()

  const checkWidth = useCallback(() => {
    const el = elementRef.current

    if (el) {
      const width = el.offsetWidth
      const below = width < breakpoint
      setIsBelowBreakpoint((prev) => (prev !== below ? below : prev))
    }
  }, [breakpoint])

  useIsomorphicEffect(() => {
    const el = elementRef.current
    if (!el) return

    const observer = new ResizeObserver(() => {
      debounce(() => {
        checkWidth()
      }, debounceTime)
    })

    observer.observe(el)
    checkWidth() // initial check

    return () => {
      observer.disconnect()
    }
  }, [checkWidth, debounceTime])

  return { elementRef, isBelowBreakpoint }
}

export default useBreakpointDetector
